import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, PlayCircle, Home, GraduationCap, BookOpenText, UserRound, BookMarked, ShoppingBag, LifeBuoy, LogIn } from "lucide-react";
import { Wordmark, LangSwitch } from "./Layout";
import { useMenuA11y } from "../lib/menu";

const ITEMS = [["/", "home", Home], ["/about/learning", "learning", GraduationCap], ["/about/methodology", "methodology", BookOpenText], ["/about/author", "author", UserRound], ["/cases", "cases", BookMarked], ["/catalogue", "catalogue", ShoppingBag], ["/demo", "demo", PlayCircle], ["/help", "help", LifeBuoy], ["/login", "sign_in", LogIn]];

const itemCls = ({ isActive }) => [
  "group relative flex min-h-[48px] items-center gap-3 rounded-2xl px-4 text-base font-medium text-ink/85 transition-colors hover:bg-sand hover:text-ink",
  "lg:min-h-control lg:gap-1.5 lg:rounded-full lg:px-2.5 lg:text-[15px] xl:px-3",
  isActive ? "bg-sand font-semibold text-ink before:absolute before:inset-y-3 before:left-1.5 before:w-1 before:rounded-full before:bg-clay lg:bg-transparent lg:before:inset-x-3 lg:before:inset-y-auto lg:before:bottom-1.5 lg:before:left-3 lg:before:h-0.5 lg:before:w-auto" : "",
].join(" ");

export function PublicHeader({ ids = {} }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null), panelRef = useRef(null);
  const close = () => setOpen(false);
  useMenuA11y(open, close, toggleRef, panelRef);
  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-[2px] lg:hidden" onClick={close} aria-hidden data-testid="public-menu-backdrop" />}
      <header className={`relative z-40 ${open ? "bg-background lg:bg-transparent" : ""}`} data-testid="public-header">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Wordmark to="/" />
          <div className="ml-auto flex items-center gap-2">
            <nav id="public-nav" ref={panelRef} aria-label={t("nav.main")} data-testid={ids.nav || "public-nav"}
              className={`${open ? "flex panel-in" : "hidden"} absolute inset-x-3 top-[68px] max-h-[calc(100dvh-84px)] flex-col gap-1 overflow-y-auto rounded-3xl border bg-card p-3 shadow-lift lg:static lg:flex lg:max-h-none lg:flex-row lg:items-center lg:gap-0 lg:overflow-visible lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none`}>
              <div className="mb-1 flex items-center justify-between border-b px-2 pb-2 lg:hidden">
                <span className="eyebrow">{t("b7.menu")}</span>
                <button type="button" onClick={() => { close(); toggleRef.current?.focus(); }} data-testid={ids.close || "public-menu-close"} aria-label={t("b7.close_menu")}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border bg-card hover:bg-sand"><X className="h-5 w-5" aria-hidden /></button>
              </div>
              {ITEMS.map(([to, k, Icon]) => (
                <NavLink key={k} to={to} end={to === "/"} data-testid={ids[k] || `pub-nav-${k}`} onClick={close} className={itemCls}>
                  <Icon className={`h-5 w-5 shrink-0 text-clay lg:h-4 lg:w-4 ${k === "demo" ? "" : "lg:hidden"}`} aria-hidden />{t(`b5.nav.${k}`)}
                </NavLink>
              ))}
            </nav>
            <LangSwitch />
            <button ref={toggleRef} type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-full border bg-card shadow-xs hover:bg-sand lg:hidden" aria-expanded={open} aria-controls="public-nav"
              aria-label={open ? t("b7.close_menu") : t("nav.menu")} data-testid={ids.toggle || "public-menu-toggle"} onClick={() => setOpen(!open)}>{open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}</button>
          </div>
        </div>
      </header>
    </>
  );
}

export function ExploreDemo({ testid = "explore-demo-btn", className = "" }) {
  const { t } = useTranslation();
  return (
    <Link to="/demo" data-testid={testid} className={`btn btn-lg btn-ink hover:-translate-y-0.5 ${className}`}>
      <PlayCircle className="h-5 w-5 shrink-0" aria-hidden />{t("b5.explore_demo")}
    </Link>
  );
}
